import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { BackOsom } from "@/components/BackOsom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Building2, FileSignature, Briefcase, Clock, FolderOpen, ShieldCheck, Sparkles } from "lucide-react";

const FEATURES = [
  { icon: Building2, title: "Inscription entreprises", desc: "Onboarding multi-étapes des membres avec SIRET, contacts et territoire Outre-mer." },
  { icon: FileSignature, title: "Conventions de mise à disposition", desc: "Création, signature, suivi et versioning – conforme L1253 du Code du travail." },
  { icon: Briefcase, title: "Missions & timesheets", desc: "Planification des missions, saisie des heures et validation par les employeurs." },
  { icon: Clock, title: "Abonnements & crédits", desc: "Gestion annuelle, achats de crédits services et soldes par entreprise." },
  { icon: FolderOpen, title: "GED ESS confidentielle", desc: "Classement, versioning, confidentialité médicale restreinte et audit trail complet." },
  { icon: ShieldCheck, title: "KPI & conformité", desc: "RH, finance, missions, GED, audit – pilotez votre groupement en temps réel." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <a href="/presentation-oscop-ge.html" data-testid="landing-presentation" className="hidden md:inline-flex text-sm font-medium text-slate-700 underline underline-offset-4">
              Présentation & simulateur
            </a>
            <Link to="/login"><Button variant="ghost" data-testid="landing-login" className="text-slate-700">Se connecter</Button></Link>
            <Link to="/register">
              <Button data-testid="landing-register" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                Rejoindre <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden gold-gradient gold-radial">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6">
          <BackOsom testId="landing-back-osom" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-24 lg:pt-20 lg:pb-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 border border-[#C5A059]/30 px-3 py-1 text-xs font-medium text-[#A88846]" data-testid="hero-badge">
              <Sparkles className="h-3.5 w-3.5" />
              SCIC SAS · OBJECTIF SCOP OUTREMER
            </div>
            <h1 className="mt-6 text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Le SaaS du <span className="text-[#C5A059]">groupement d&apos;employeurs</span> coopératif d&apos;Outre-mer.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              O&apos;SCOP GE pilote l&apos;ensemble du cycle : adhésions, mises à disposition, missions, paie mutualisée, GED conforme et appels à contribution au coût réel.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a href="/presentation-oscop-ge.html#modele" data-testid="hero-cta-simulator" className="inline-flex items-center justify-center rounded-md bg-[#17352e] px-8 h-12 text-sm font-medium text-white hover:bg-[#284b40] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
                Simuler mon budget <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </a>
              <Link to="/register">
                <Button size="lg" data-testid="hero-cta-primary" className="bg-[#C5A059] hover:bg-[#A88846] text-white px-8 h-12">
                  Démarrer maintenant <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" data-testid="hero-cta-secondary" className="border-slate-300 text-slate-700 h-12">
                  Connexion gestionnaire
                </Button>
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {[
                ["6+", "Territoires Outre-mer"],
                ["8", "Profils & rôles"],
                ["100%", "Traçabilité audit"],
                ["ESS", "Conformité L1253"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="text-3xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{n}</div>
                  <div className="text-sm text-slate-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#C5A059] mb-3">Plateforme intégrée</div>
            <h2 className="text-4xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Un outil. Tout le cycle GE.
            </h2>
            <p className="mt-3 text-slate-600">De l&apos;inscription à la facturation au coût réel — un seul tableau de bord coopératif.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="kpi-card bg-white border border-slate-200 rounded-xl p-6">
                <div className="h-10 w-10 rounded-lg bg-[#FDF9F1] border border-[#C5A059]/30 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-[#C5A059]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row justify-between items-center gap-4">
          <Logo />
          <div className="text-xs text-slate-500">© 2026 O&apos;SCOP GE — OBJECTIF SCOP OUTREMER · SCIC SAS · ESS Outre-mer</div>
        </div>
      </footer>
    </div>
  );
}
